<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useCurrency } from 'dashboard/composables/useCurrency';
import { useAlert } from 'dashboard/composables';
import { createLatestRequest } from 'dashboard/helper/latestRequest';
import DealReportsAPI from 'dashboard/api/dealReports';
import Button from 'dashboard/components-next/button/Button.vue';
import SummaryCards from './components/SummaryCards.vue';
import ConversionFunnel from './components/ConversionFunnel.vue';
import LossReasonsChart from './components/LossReasonsChart.vue';
import AgentsPerformance from './components/AgentsPerformance.vue';
import DealsTimeline from './components/DealsTimeline.vue';
import ForecastWidget from './components/ForecastWidget.vue';
import VelocityChart from './components/VelocityChart.vue';
import AgingOverview from './components/AgingOverview.vue';
import GoalProgress from './components/GoalProgress.vue';

const store = useStore();
const { t, locale } = useI18n();
const browserLocale = computed(() => locale.value.replace('_', '-'));
const { currency } = useCurrency();

// State
const tracker = createLatestRequest();
const isLoading = ref(false);
const selectedFunnelId = ref(null);
const selectedPeriod = ref('30d');
const customStartDate = ref('');
const customEndDate = ref('');

// Report data
const summaryData = ref(null);
// Funil de conversão vem do livro de eventos (conversion_funnel), não da
// foto da etapa atual (by_stage) — antes o cálculo pelo ledger existia e
// nunca era exibido.
const conversionData = ref(null);
const agentData = ref(null);
const lossReasonsData = ref(null);
const timelineData = ref(null);
const forecastData = ref(null);
const velocityData = ref(null);
const agingData = ref(null);

// Computed
const funnels = computed(() => store.getters['funnels/getFunnels'] || []);

const periodOptions = computed(() => [
  { value: '7d', label: t('DEAL_REPORTS.FILTERS.PERIOD_7D') },
  { value: '30d', label: t('DEAL_REPORTS.FILTERS.PERIOD_30D') },
  { value: '90d', label: t('DEAL_REPORTS.FILTERS.PERIOD_90D') },
  { value: '365d', label: t('DEAL_REPORTS.FILTERS.PERIOD_1Y') },
  { value: 'custom', label: t('DEAL_REPORTS.FILTERS.PERIOD_CUSTOM') },
]);

// Data local (YYYY-MM-DD), sem toISOString: em UTC o dia virava o seguinte à noite.
const toLocalDate = date => {
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const isCustomIncomplete = computed(
  () =>
    selectedPeriod.value === 'custom' &&
    !(customStartDate.value && customEndDate.value)
);

const dateRange = computed(() => {
  const today = new Date();
  const startDate = new Date(today);

  switch (selectedPeriod.value) {
    case '7d':
      startDate.setDate(today.getDate() - 7);
      break;
    case '90d':
      startDate.setDate(today.getDate() - 90);
      break;
    case '365d':
      startDate.setFullYear(today.getFullYear() - 1);
      break;
    case 'custom':
      return {
        start_date: customStartDate.value,
        end_date: customEndDate.value,
      };
    default:
      startDate.setDate(today.getDate() - 30);
  }

  return {
    start_date: toLocalDate(startDate),
    end_date: toLocalDate(today),
  };
});

const showCustomDatePicker = computed(() => selectedPeriod.value === 'custom');

// Methods
const resetBlocks = () => {
  [
    summaryData,
    conversionData,
    agentData,
    lossReasonsData,
    timelineData,
    forecastData,
    velocityData,
    agingData,
  ].forEach(block => {
    block.value = null;
  });
};

const fetchReports = async () => {
  const id = tracker.next();

  // Período personalizado incompleto: avisa e zera, não mantém números antigos.
  if (isCustomIncomplete.value) {
    resetBlocks();
    isLoading.value = false;
    return;
  }
  if (!selectedFunnelId.value) {
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  const params = {
    ...dateRange.value,
    funnel_id: selectedFunnelId.value,
  };

  const blocks = [
    [summaryData, () => DealReportsAPI.getSummary(params)],
    [conversionData, () => DealReportsAPI.getConversionFunnel(params)],
    [agentData, () => DealReportsAPI.getByAgent(params)],
    [lossReasonsData, () => DealReportsAPI.getLossReasons(params)],
    [timelineData, () => DealReportsAPI.getTimeline(params)],
    [forecastData, () => DealReportsAPI.getForecast(params)],
    [velocityData, () => DealReportsAPI.getVelocity(params)],
    [agingData, () => DealReportsAPI.getAging(params)],
  ];

  const results = await Promise.allSettled(blocks.map(([, call]) => call()));
  if (!tracker.isLatest(id)) return;

  let failed = 0;
  results.forEach((result, index) => {
    blocks[index][0].value =
      result.status === 'fulfilled' ? result.value.data : null;
    if (result.status === 'rejected') failed += 1;
  });
  if (failed) useAlert(t('DEAL_REPORTS.PARTIAL_ERROR', { count: failed }));
  isLoading.value = false;
};

const formatCurrency = value => {
  return new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currency.value,
  }).format(value || 0);
};

// Qualquer mudança de filtro recarrega (o aviso de "Personalizado" incompleto
// é tratado dentro do fetchReports).
watch(
  [selectedFunnelId, selectedPeriod, customStartDate, customEndDate],
  fetchReports
);

// Carrega ao montar: escolher o 1º funil dispara o watch acima (sem busca dupla).
onMounted(async () => {
  await store.dispatch('funnels/get');
  if (funnels.value.length > 0) {
    selectedFunnelId.value = funnels.value[0].id;
  }
});
</script>

<template>
  <div
    class="deal-reports-dashboard p-6 bg-slate-50 dark:bg-slate-900 min-h-full"
  >
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-slate-900 dark:text-white mb-2">
        {{ t('DEAL_REPORTS.TITLE') }}
      </h1>
      <p class="text-slate-600 dark:text-slate-400">
        {{ t('DEAL_REPORTS.DESCRIPTION') }}
      </p>
    </div>

    <!-- Filters -->
    <div
      class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4 mb-6"
    >
      <div class="flex flex-wrap items-center gap-4">
        <!-- Funnel Selector -->
        <div class="flex-1 min-w-48">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('DEAL_REPORTS.FILTERS.FUNNEL') }}
          </label>
          <select
            v-model="selectedFunnelId"
            class="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          >
            <option
              v-for="funnel in funnels"
              :key="funnel.id"
              :value="funnel.id"
            >
              {{ funnel.name }}
            </option>
          </select>
        </div>

        <!-- Period Selector -->
        <div class="flex-1 min-w-48">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('DEAL_REPORTS.FILTERS.PERIOD') }}
          </label>
          <select
            v-model="selectedPeriod"
            class="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          >
            <option
              v-for="option in periodOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Custom Date Range -->
        <template v-if="showCustomDatePicker">
          <div class="flex-1 min-w-40">
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('DEAL_REPORTS.FILTERS.START_DATE') }}
            </label>
            <input
              v-model="customStartDate"
              type="date"
              class="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div class="flex-1 min-w-40">
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('DEAL_REPORTS.FILTERS.END_DATE') }}
            </label>
            <input
              v-model="customEndDate"
              type="date"
              class="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </template>

        <!-- Refresh Button -->
        <div class="flex items-end">
          <Button
            icon="i-lucide-refresh-cw"
            :is-loading="isLoading"
            slate
            faded
            @click="fetchReports"
          >
            {{ t('DEAL_REPORTS.REFRESH') }}
          </Button>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="h-0.5 w-full animate-pulse bg-n-brand mb-4" />

    <p
      v-if="isCustomIncomplete"
      class="mb-4 text-sm text-amber-700 dark:text-amber-400"
    >
      {{ t('DEAL_REPORTS.FILTERS.CUSTOM_INCOMPLETE') }}
    </p>

    <!-- Loading State -->
    <div
      v-if="isLoading && !summaryData"
      class="flex items-center justify-center py-20"
    >
      <div class="text-center">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"
        />
        <p class="text-slate-500">{{ t('DEAL_REPORTS.LOADING') }}</p>
      </div>
    </div>

    <!-- Dashboard Content -->
    <div v-else class="space-y-6">
      <!-- Summary Cards -->
      <SummaryCards
        v-if="summaryData"
        :data="summaryData"
        :format-currency="formatCurrency"
      />

      <!-- Charts Row 1 -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Won vs Lost Timeline -->
        <DealsTimeline
          v-if="timelineData"
          :data="timelineData"
          :format-currency="formatCurrency"
        />

        <!-- Conversion Funnel -->
        <ConversionFunnel v-if="conversionData" :data="conversionData" />
      </div>

      <!-- Charts Row 2 -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Loss Reasons -->
        <LossReasonsChart v-if="lossReasonsData" :data="lossReasonsData" />

        <!-- Agents Performance -->
        <AgentsPerformance
          v-if="agentData"
          :data="agentData"
          :format-currency="formatCurrency"
        />
      </div>

      <!-- Pipeline Metrics Row -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Pipeline Forecast -->
        <ForecastWidget
          v-if="forecastData"
          :data="forecastData"
          :format-currency="formatCurrency"
        />

        <!-- Pipeline Velocity -->
        <VelocityChart v-if="velocityData" :data="velocityData" />
      </div>

      <!-- Aging & Goals Row -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Deal Aging -->
        <AgingOverview v-if="agingData" :data="agingData" />

        <!-- Sales Goals -->
        <GoalProgress :funnel-id="selectedFunnelId" />
      </div>
    </div>
  </div>
</template>
